import { matchOp, resolveFormVisibility } from '@servicetim/domain';
import type { Condition, CreateTicketInput, UpdateTicket } from '@servicetim/shared';
import { flattenSections } from './normalize';
import type { SchemaFormSection, SchemaFormValues } from './types';

export const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'number' && Number.isNaN(value));

/** `visibleWhen` for one field against the current values (`matchOp` from the domain package). */
export const evaluateCondition = (condition: Condition, values: SchemaFormValues): boolean =>
  matchOp(condition.op, values[condition.field], condition.value);

/**
 * Visibility of every placed field. Chains resolve too: a field whose condition points at a
 * hidden field is hidden as well (ADR-0013 §4).
 */
export function computeVisibility(
  sections: readonly SchemaFormSection[],
  values: SchemaFormValues,
): { visible: Set<string>; hidden: Set<string> } {
  return resolveFormVisibility(
    {
      sections: sections.map((s) => ({
        fields: s.fields.map((f) => ({ key: f.key, visibleWhen: f.visibleWhen })),
      })),
    },
    values,
  );
}

export type EmptyValueMode = 'omit' | 'null';

export interface PreparedValues {
  values: SchemaFormValues;
  /** Keys stripped because their `visibleWhen` was false. */
  hidden: string[];
}

/**
 * Values to submit: hidden fields are dropped, read-only fields are dropped, and empty values
 * are omitted (`create`) or sent as `null` (`patch`, where null clears the stored value).
 */
export function prepareSubmitValues(
  sections: readonly SchemaFormSection[],
  raw: SchemaFormValues,
  options: { emptyValue?: EmptyValueMode } = {},
): PreparedValues {
  const mode = options.emptyValue ?? 'omit';
  const fields = flattenSections(sections);
  const { hidden } = computeVisibility(sections, raw);
  const values: SchemaFormValues = {};
  for (const field of fields) {
    if (hidden.has(field.key) || field.readOnly) continue;
    const value = raw[field.key];
    if (isEmptyValue(value)) {
      if (mode === 'null') values[field.key] = null;
      continue;
    }
    values[field.key] = value;
  }
  return { values, hidden: [...hidden] };
}

export interface TicketValueSplit {
  system: Pick<
    UpdateTicket,
    'summary' | 'description' | 'priorityId' | 'assigneeId' | 'labels' | 'dueAt'
  >;
  /** Custom values by `cf_` key. */
  fields: Record<string, unknown>;
}

/**
 * Splits flat form values into the ticket DTO shape: system keys become camelCase properties,
 * `cf_*` keys go under `fields`. Works for create (`{ ...system, fields }`) and patch bodies.
 */
export function splitTicketValues(values: SchemaFormValues): TicketValueSplit {
  const system: TicketValueSplit['system'] = {};
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    switch (key) {
      case 'summary':
        system.summary = value as string;
        break;
      case 'description':
        system.description = value as string | null;
        break;
      case 'priority_id':
        system.priorityId = value as string | null;
        break;
      case 'assignee_id':
        system.assigneeId = value as string | null;
        break;
      case 'labels':
        system.labels = value as string[];
        break;
      case 'due_at':
        system.dueAt = value as string | null;
        break;
      default:
        if (key.startsWith('cf_')) fields[key] = value;
    }
  }
  return { system, fields };
}

/** `CreateTicket` body from form values plus the target project / issue type. */
export function toCreateTicketInput(
  values: SchemaFormValues,
  target: { projectId: string; issueTypeId: string },
): CreateTicketInput {
  const { system, fields } = splitTicketValues(values);
  return {
    projectId: target.projectId,
    issueTypeId: target.issueTypeId,
    summary: system.summary ?? '',
    description: system.description ?? null,
    priorityId: system.priorityId,
    assigneeId: system.assigneeId,
    labels: system.labels ?? [],
    dueAt: system.dueAt,
    fields,
  };
}

/** Form default values from a ticket: system snake_case keys + its `fields`. */
export function ticketToFormValues(ticket: {
  summary?: string;
  description?: string | null;
  priorityId?: string | null;
  assigneeId?: string | null;
  labels?: string[];
  dueAt?: string | null;
  fields?: Record<string, unknown>;
}): SchemaFormValues {
  return {
    summary: ticket.summary,
    description: ticket.description ?? undefined,
    priority_id: ticket.priorityId ?? undefined,
    assignee_id: ticket.assigneeId ?? undefined,
    labels: ticket.labels,
    due_at: ticket.dueAt ?? undefined,
    ...(ticket.fields ?? {}),
  };
}
